import React, { useState, useEffect, useRef } from 'react'

import { Typography } from '@material-ui/core'
import {
  WizardForm,
  WizardFormStep,
  CreateDialog,
} from '@react-admin/ra-form-layout'
import { print } from 'graphql'
import { processInvoiceAdvanceBulkRequest } from 'provider/queries/hasuraQueries'
import {
  useQuery,
  Loading,
  Error,
  useTranslate,
  useLocale,
  useRedirect,
  Confirm,
} from 'react-admin'
import { post as getFromHasura } from 'utils/axios'

import { orange } from '../../constants/colorConstants'
import tracker from '../../utils/tracker'
import { useRefreshContent } from '../lists/refreshContent'
import {
  NONE,
  ADVANCE_LIST_STEP,
  INVOICE_FORM_STEP,
  FILE_UPLOAD_STEP,
} from './createDialogConstants'
import { CreateInvoiceDialogTitle } from './createInvoiceDialogTitle'
import { CreateInvoiceDialogToolbar } from './createInvoiceDialogToolbar'
import { InvoiceCreationFormStep } from './steps/invoiceCreationFormStep'
import { InvoiceCreationResultStep } from './steps/invoiceCreationResultStep'
import { InvoiceCreationUploadStep } from './steps/invoiceCreationUploadStep'
import { InvoicesSummaryStep } from './steps/InvoicesSummaryStep'
import useStyles from './styles'

function buildFileUploadState(
  files,
  uploading = false,
  uploadStatus = false,
  open = false
) {
  return {
    files,
    uploading,
    uploadStatus,
    open,
  }
}

const invoiceModalInitialState = {
  invoiceNumber: undefined,
  importerName: undefined,
  importerID: undefined,
  issueDate: undefined,
  dueDate: undefined,
  amount: undefined,
  purchaseOrderNumber: undefined,
}

export const CreateInvoiceDialog = ({
  onCountRefresh,
  tabsCount,
  ...props
}) => {
  const classes = useStyles()
  const translate = useTranslate()
  const locale = useLocale()
  const redirect = useRedirect()

  const [, setRefreshContent] = useRefreshContent()

  const [form, setForm] = useState(undefined)
  const [invoiceList, setInvoiceList] = useState([])
  const [lastInvoiceListCount, setListInvoiceListCount] = useState([])
  const [currentInvoiceIndex, setCurrentInvoiceIndex] = useState(NONE)
  const [showConfirmCancel, setShowConfirmCancel] = useState(false)

  const [stepNumber, setStepNumber] = useState(1)
  const [forceChangeStep, setForceChangeStep] = useState(NONE)
  const [errorSave, setErrorSave] = useState(undefined)
  const [isFinished, setIsFinished] = useState(false)
  const [saving, setSaving] = useState(false)
  const [advanceInvoiceModalState, setAdvanceInvoiceModal] = useState(
    invoiceModalInitialState
  )
  const [savedFiles, setSaveFiles] = useState([])
  const [timestamp] = useState(Math.round(new Date().getTime() / 1000))
  const [state, setState] = useState({
    ...buildFileUploadState(savedFiles),
  })
  const [changesCount, setChangedCount] = useState(0)
  const [advanceCount, setAdvancedCount] = useState()
  const [waitForPolling, setWaitForPolling] = useState(true)
  const countIntervalRef = useRef()

  const { basePath } = props

  const { data, loading, error } = useQuery({
    type: 'getList',
    resource: 'creditlines',
    payload: {
      pagination: { page: 1, perPage: 100000 },
      sort: { field: 'importerName', order: 'ASC' },
    },
  })

  useEffect(() => {
    const formLocal = localStorage.getItem('invoiceAdvanceModal')
    if (formLocal && form) {
      setTimeout(() => {
        fillForm(JSON.parse(formLocal))
      }, 100)
    }
  }, [form])

  useEffect(() => {
    setAdvancedCount(tabsCount?.advanced)
  }, [tabsCount?.advanced])

  useEffect(() => {
    if (
      advanceCount !== undefined &&
      advanceCount !== 0 &&
      advanceCount !== tabsCount?.advanced
    ) {
      setWaitForPolling(false)
      clearInterval(countIntervalRef.current)
      setRefreshContent(true)
    }
  }, [advanceCount, tabsCount?.advanced, setRefreshContent])

  if (loading) return <Loading />
  if (error) return <Error error={error} />

  const clearInvoices = () => {
    setInvoiceList([])
    setCurrentInvoiceIndex(NONE)
  }

  const clearForm = () => {
    setChangedCount(0)
    setStepNumber(ADVANCE_LIST_STEP)
    setAdvanceInvoiceModal(invoiceModalInitialState)
    setSaveFiles([])
    setState({
      ...buildFileUploadState(savedFiles),
    })
    if (form) {
      form.change('files', [])
      form.change('invoiceNumber', undefined)
      form.change('creditLineId', undefined)
      form.change('issueDate', undefined)
      form.change('dueDate', undefined)
      form.change('amount', undefined)
      form.change('purchaseOrderNumber', undefined)
    }
    localStorage.removeItem('invoiceAdvanceModal')
  }

  const fillForm = (invoice) => {
    setSaveFiles(invoice.files || [])
    setChangedCount(0)
    setAdvanceInvoiceModal({
      invoiceNumber: invoice.invoiceNumber,
      importerName: invoice.importerName,
      importerID: invoice.importerID,
      issueDate: invoice.issueDate,
      dueDate: invoice.dueDate,
      amount: invoice.amount,
      purchaseOrderNumber: invoice.purchaseOrderNumber,
    })

    setState({
      ...buildFileUploadState(savedFiles),
    })
    if (form) {
      form.change('files', invoice.files)
      form.change('invoiceNumber', invoice.invoiceNumber)
      form.change('creditLineId', invoice.creditLineId)
      form.change('issueDate', invoice.issueDate)
      form.change('dueDate', invoice.dueDate)
      form.change('amount', invoice.amount)
      form.change('purchaseOrderNumber', invoice.purchaseOrderNumber)
    }
  }

  const onSuccess = () => {
    setListInvoiceListCount(invoiceList?.length)
    setSaving(false)
    setErrorSave(false)
    setIsFinished(true)
    clearForm()
    clearInvoices()

    const intervalId = setInterval(() => {
      onCountRefresh()
    }, 1000)

    countIntervalRef.current = intervalId
  }

  const onFailure = () => {
    setSaving(false)
    setErrorSave(true)
    setIsFinished(true)
  }

  const transform = (data) => {
    tracker.event({
      action: 'invoice_advance_request_dialog_submit_button_clicked',
      category: 'invoiceAdvanceRequest',
      label: 'filesUploaded',
      value: savedFiles.length,
    })
    return data
  }

  const completionValue = () => {
    let completion = 0
    if (advanceInvoiceModalState.invoiceNumber) completion += 1
    if (advanceInvoiceModalState.importerName) completion += 2
    if (advanceInvoiceModalState.issueDate) completion += 4
    if (advanceInvoiceModalState.dueDate) completion += 8
    if (advanceInvoiceModalState.amount) completion += 16
    if (advanceInvoiceModalState.purchaseOrderNumber) completion += 32

    return completion
  }

  const resolveClose = () => {
    tracker.event({
      action:
        'invoice_advance_request_confirmation_dialog_close_button_clicked',
      category: 'invoiceAdvanceRequest',
    })
    setIsFinished(false)
    setErrorSave(false)
    clearForm()
    clearInvoices()
  }

  const resolveCancel = () => {
    if (stepNumber === INVOICE_FORM_STEP)
      tracker.event({
        action: 'invoice_advance_request_dialog_cancel_button_clicked',
        category: 'invoiceAdvanceRequest',
        label: 'CompletionLevel',
        value: completionValue(),
      })
    if (stepNumber === FILE_UPLOAD_STEP)
      tracker.event({
        action: 'invoice_advance_request_dialog_second_step_cancel_clicked',
        category: 'upload',
        label: 'filesUploaded',
        value: savedFiles.length,
      })
    setIsFinished(false)
    setErrorSave(false)
    clearForm()
  }

  const handleConfirmExitOK = () => {
    setShowConfirmCancel(false)
    resolveCancel(props)
    clearInvoices()
    redirect(basePath)
  }

  const handleConfirmExitCancel = () => {
    setShowConfirmCancel(false)
  }

  const handleCloseDialog = () => {
    if (isFinished) {
      resolveClose(props)
      return redirect(basePath)
    }

    if (
      invoiceList.length > 0 &&
      (stepNumber === INVOICE_FORM_STEP || stepNumber === FILE_UPLOAD_STEP)
    ) {
      resolveCancel(props)
      return setForceChangeStep(ADVANCE_LIST_STEP)
    }

    if (invoiceList.length === 0) {
      resolveCancel(props)
      clearInvoices()
      return redirect(basePath)
    }

    setShowConfirmCancel(true)
  }

  const handleEnter = () => {
    setForceChangeStep(INVOICE_FORM_STEP)
  }

  const handleBack = () => {
    tracker.event({
      action: 'invoice_advance_request_dialog_back_button_clicked',
      category: 'invoiceAdvanceRequest',
    })
    setIsFinished(false)
    setErrorSave(false)
  }

  const handleNext = () => {
    tracker.event({
      action: 'invoice_advance_request_dialog_next_button_clicked',
      category: 'invoiceAdvanceRequest',
    })
    setIsFinished(false)
    setErrorSave(false)
  }

  const onInvoiceAdvanceRequestChange = ({ target: { name, value } }) => {
    const newInvoiceAdvanceState = {
      ...advanceInvoiceModalState,
    }
    if (name === 'importerName') {
      const importer = data.filter((item) => item.id === value)
      if (importer.length >= 0) {
        newInvoiceAdvanceState.importerName = importer[0].importerName
        newInvoiceAdvanceState.importerID = importer[0].id
        setAdvanceInvoiceModal(newInvoiceAdvanceState)
      }
    } else {
      newInvoiceAdvanceState[name] = value
      setAdvanceInvoiceModal(newInvoiceAdvanceState)
    }
    localStorage.setItem(
      'invoiceAdvanceModal',
      JSON.stringify(newInvoiceAdvanceState)
    )
  }

  const handleAdd = () => {
    setCurrentInvoiceIndex(NONE)
    clearForm()
    setForceChangeStep(INVOICE_FORM_STEP)
  }

  const handleEditClick = (i, invoice) => {
    setCurrentInvoiceIndex(i)
    fillForm(invoice)
    setForceChangeStep(INVOICE_FORM_STEP)
  }

  const handleUploadFilesClick = (i, invoice) => {
    setCurrentInvoiceIndex(i)
    fillForm(invoice)
    setForceChangeStep(FILE_UPLOAD_STEP)
  }

  const handleRemoveClick = (index) => {
    setInvoiceList(invoiceList.filter((invoice, i) => i !== index))
  }

  const parseHasuraData = (data) => {
    return data.map((invoice) => ({
      invoice: {
        id: invoice.invoiceNumber,
        amount: invoice.amount,
        issueDate: invoice.issueDate,
        dueDate: invoice.dueDate,
      },
      businessProductId: invoice.creditLineId,
      documents: invoice.files.map((file) => ({
        name: file.fileId,
        location: file.url,
      })),
    }))
  }

  const handleSave = () => {
    setSaving(true)

    const parsedHasuraData = parseHasuraData(invoiceList)

    let invoiceAdvanceBulkRequest = getFromHasura({
      payload: print(processInvoiceAdvanceBulkRequest),
      variables: {
        command: {
          requests: parsedHasuraData,
        },
        commandName: 'processInvoiceAdvanceBulkRequest',
      },
    })

    invoiceAdvanceBulkRequest.then((res) => {
      if (res?.data.invoiceAdvanceMutation?.data.invoicesCreated.length > 0)
        onSuccess()
      else onFailure()
    })

    localStorage.removeItem('invoiceAdvanceModal')
  }

  const handleApply = () => {
    const currentInvoice = form.getState().values

    if (currentInvoiceIndex === NONE) {
      setInvoiceList([...invoiceList, currentInvoice])
    } else {
      setInvoiceList(
        invoiceList.map((invoice, i) =>
          i === currentInvoiceIndex ? currentInvoice : invoice
        )
      )
    }
    setCurrentInvoiceIndex(NONE)
    setForceChangeStep(ADVANCE_LIST_STEP)
  }

  const Progress = ({ currentStep, onStepClick }) => {
    useEffect(() => {
      setStepNumber(currentStep)
    }, [setStepNumber, currentStep])

    useEffect(() => {
      if (forceChangeStep !== NONE) {
        onStepClick(forceChangeStep)
        setForceChangeStep(NONE)
      }
      // eslint-disable-next-line
    }, [forceChangeStep, stepNumber, onStepClick])

    return <></>
  }

  return (
    <>
      <CreateDialog
        {...props}
        onFailure={onFailure}
        onSuccess={onSuccess}
        onEnter={handleEnter}
        onClose={handleCloseDialog}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
        transform={transform}
        title={
          <CreateInvoiceDialogTitle
            isFinished={isFinished}
            errorSave={errorSave}
            stepNumber={stepNumber}
            invoiceList={invoiceList}
          />
        }
      >
        <WizardForm
          progress={Progress}
          toolbar={(localProps) => {
            return (
              <CreateInvoiceDialogToolbar
                {...localProps}
                basePath={props.basePath}
                handleCancel={handleCloseDialog}
                handleClose={handleCloseDialog}
                handleBack={handleBack}
                handleNext={handleNext}
                handleAdd={handleAdd}
                handleSave={handleSave}
                handleApply={handleApply}
                isFinished={isFinished}
                errorSave={errorSave}
                stepNumber={stepNumber}
                savedFiles={savedFiles}
                invoiceList={invoiceList}
                saving={saving}
                waitForPolling={waitForPolling}
              />
            )
          }}
        >
          <WizardFormStep>
            {errorSave ? (
              <Typography className={classes.errorMessage}>
                {translate('resources.invoices.actions.create.response.error')}
              </Typography>
            ) : (
              <></>
            )}

            {stepNumber === ADVANCE_LIST_STEP && (!isFinished || errorSave) && (
              <InvoicesSummaryStep
                classes={classes}
                invoices={invoiceList}
                data={data}
                onEditClick={handleEditClick}
                onUploadFilesClick={handleUploadFilesClick}
                onRemoveClick={handleRemoveClick}
                key={`${JSON.stringify(data)}key`}
              />
            )}

            {stepNumber === ADVANCE_LIST_STEP && isFinished && !errorSave && (
              <InvoiceCreationResultStep
                classes={classes}
                invoice={advanceInvoiceModalState.invoiceNumber}
                importerName={advanceInvoiceModalState.importerName}
                invoiceCount={lastInvoiceListCount}
              />
            )}
          </WizardFormStep>
          <WizardFormStep>
            <InvoiceCreationFormStep
              locale={locale}
              classes={classes}
              data={data}
              key={`${JSON.stringify(data)}key`}
              onChangeImporterName={onInvoiceAdvanceRequestChange}
              onChangeInvoice={onInvoiceAdvanceRequestChange}
              onChangeIssueDate={onInvoiceAdvanceRequestChange}
              onChangeDueDate={onInvoiceAdvanceRequestChange}
              onChangeAmount={onInvoiceAdvanceRequestChange}
              onChangePurchaseOrderNumber={onInvoiceAdvanceRequestChange}
              setForm={setForm}
              selectedImporterID={advanceInvoiceModalState.importerID}
            />
          </WizardFormStep>
          <WizardFormStep>
            <InvoiceCreationUploadStep
              resource={props.resource}
              classes={classes}
              invoiceId={advanceInvoiceModalState.invoiceNumber}
              savedFiles={savedFiles}
              setSaveFiles={setSaveFiles}
              state={state}
              setState={setState}
              buildFileUploadState={buildFileUploadState}
              changesCount={changesCount}
              setChangedCount={setChangedCount}
              timestamp={timestamp}
            />
          </WizardFormStep>
        </WizardForm>
      </CreateDialog>
      <Confirm
        isOpen={showConfirmCancel}
        title={'resources.invoices.actions.create.confirmCancelTitle'}
        content={
          (invoiceList?.length ?? 0) !== 1
            ? 'resources.invoices.actions.create.confirmCancelPlural'
            : 'resources.invoices.actions.create.confirmCancelSingular'
        }
        confirm={'resources.invoices.actions.create.yesDiscard'}
        cancel={'resources.invoices.actions.create.noDiscard'}
        ConfirmIcon={() => <></>}
        CancelIcon={() => <></>}
        onConfirm={handleConfirmExitOK}
        onClose={handleConfirmExitCancel}
        confirmColor={orange}
        translateOptions={{
          params: {
            invoicesCount: invoiceList?.length ?? 0,
          },
        }}
      />
    </>
  )
}
